import type {AxiosResponse} from "axios";

import CoreApi from "@/src/modules/core/api";
import type {GetListResponse, GetResponse, ParamsOptions} from "@/src/modules/core/types";

type ResourceApiOptions = {
  /** List/search path when it differs from the singular resource path (e.g. stored-vehicles). */
  listPath?: string;
};

/**
 * Path-bound CRUD API. Prefer this over overriding CoreApi.search/getById/… with a different
 * arity — those overrides are illegal under TypeScript (TS2416) and erase entity generics.
 */
export default class ResourceApi<TEntity = unknown> {
  private readonly http = new CoreApi();
  protected readonly path: string;
  protected readonly listPath: string;

  constructor(path: string, options: ResourceApiOptions = {}) {
    this.path = path;
    this.listPath = options.listPath ?? path;
  }

  search(options?: ParamsOptions): Promise<AxiosResponse<GetListResponse<TEntity>>> {
    return this.http.search(this.listPath, options);
  }

  getById(
    id: string | number,
    options?: ParamsOptions,
  ): Promise<AxiosResponse<GetResponse<TEntity>>> {
    return this.http.getById(this.path, id, options);
  }

  create(
    data: unknown,
    options: {
      headers?: Record<string, string>;
      meta?: {feature?: string; operation?: string};
    } = {},
  ): Promise<AxiosResponse<GetResponse<TEntity>>> {
    return this.http.create(this.path, data, options);
  }

  update(
    id: string | number,
    data?: unknown,
    options: {meta?: {feature?: string; operation?: string}} = {},
  ): Promise<AxiosResponse<GetResponse<TEntity>>> {
    return this.http.update(this.path, id, data, options);
  }

  delete(id: string | number, data?: unknown): Promise<AxiosResponse<unknown>> {
    return this.http.delete(this.path, id, data);
  }

  /** Unbound helpers for nested / alternate endpoints. */
  protected get(path: string, data?: unknown) {
    return this.http.get(path, data);
  }

  protected post(path: string, data: unknown) {
    return this.http.post(path, data);
  }

  protected createAt(
    path: string,
    data: unknown,
    options: {
      headers?: Record<string, string>;
      meta?: {feature?: string; operation?: string};
    } = {},
  ) {
    return this.http.create(path, data, options);
  }

  protected updateAt(
    path: string,
    id: string | number,
    data?: unknown,
    options: {meta?: {feature?: string; operation?: string}} = {},
  ) {
    return this.http.update(path, id, data, options);
  }

  protected getByIdAt(path: string, id: string | number, options?: ParamsOptions) {
    return this.http.getById(path, id, options);
  }

  protected deleteAt(path: string, id: string | number, data?: unknown) {
    return this.http.delete(path, id, data);
  }
}
